import { MantineColorsTuple } from '@mantine/core'

/**
 * Paleta RouteX — âmbar / laranja queimado.
 *
 * A cor foi escolhida por distinção, não por herança: o RouteX não tinha marca definida (o azul
 * anterior era o padrão do Mantine, não uma decisão), e os irmãos já ocupam o verde (VendaX) e o
 * azul escuro (Doclenz). Laranja não é usado por nenhum produto da casa, então os consoles ficam
 * impossíveis de confundir — o que importa quando se opera vários no mesmo dia.
 *
 * O tom 6 é o `filled` — a cor efetiva de botões, barra lateral e destaques. Ele é o âmbar
 * **queimado**, mais escuro e menos saturado que o laranja da faixa 5: laranja vivo em área
 * grande cansa a vista e rouba atenção do conteúdo. Os tons vivos ficam para realce pontual.
 */
export const routexAmber: MantineColorsTuple = [
  '#fdf7ed',
  '#fbeacd',
  '#f6d29b',
  '#f0b765',
  '#eba03a',
  '#d97706', // laranja âmbar — realces e gradiente da marca
  '#b45309', // ← primária (queimado)
  '#92400e',
  '#75330b',
  '#4a1f07',
]

/** Acento: o âmbar vivo. Usado em badges, bordas translúcidas e no gradiente da marca. */
export const routexGold: MantineColorsTuple = [
  '#fffbeb',
  '#fef3c7',
  '#fde68a',
  '#fcd34d',
  '#fbbf24',
  '#f59e0b', // âmbar vivo
  '#d97706',
  '#b45309',
  '#92400e',
  '#78350f',
]

/** Neutros levemente quentes: os cinzas puxam para o marrom, não para o azul. */
export const routexGray: MantineColorsTuple = [
  '#faf8f5',
  '#f3efe9',
  '#e8e1d7',
  '#d5c9b9',
  '#b3a494',
  '#7d6f5f',
  '#5c5145',
  '#3d352c',
  '#26211b',
  '#171310',
]

/** Tokens literais, para CSS e estilos inline — onde o tema do Mantine não alcança. */
export const RoutexTokens = {
  espresso950: '#2b1608',
  amber900: '#4a1f07',
  amber800: '#75330b',
  amber700: '#92400e',
  amber600: '#b45309',
  amber500: '#d97706',
  amber400: '#eba03a',
  gold: '#f59e0b',

  light: {
    bg: '#fdfaf6',
    surface: '#ffffff',
    surface2: '#fdfaf5',
    surface3: '#f6f1e9',
    text: '#171310',
    muted: '#7d6f5f',
    border: '#e8e1d7',
    shadow: '0 10px 30px rgba(74,31,7,.08)',
  },
  dark: {
    bg: '#15100b',
    surface: '#1e170f',
    surface2: '#241c12',
    surface3: '#33261a',
    text: '#faf6f0',
    muted: '#b3a494',
    border: '#463523',
    shadow: '0 12px 36px rgba(0,0,0,.28)',
  },

  /**
   * Ícones do menu lateral: bege acinzentado, não a primária.
   *
   * O âmbar vivo sobre o marrom escuro fica neon e compete com o item ativo; este tom quebra o
   * bloco de branco sem chamar atenção. O rótulo segue branco, então a hierarquia texto > ícone
   * se mantém.
   */
  sidebarIcon: '#b3a494',

  /** Gradiente da barra lateral: café quase preto descendo para âmbar profundo. */
  sidebarGradient: 'linear-gradient(180deg, rgba(43,22,8,.98), rgba(117,51,11,.97))',

  /** Fundo do item de menu ativo. */
  sidebarItemActive: 'linear-gradient(90deg, rgba(217,119,6,.88), rgba(180,83,9,.72))',

  /**
   * Fundo da área de conteúdo: dois halos radiais sobre a cor base — um dourado no alto à
   * direita, um âmbar embaixo à esquerda. Multi-camada na shorthand `background` (gradientes
   * primeiro, cor por último).
   *
   * A prop `mainBackground` do layout é um valor único, não um par claro/escuro — quem escolhe
   * entre as duas variantes é o `colorScheme` no App.
   */
  mainBackground:
    'radial-gradient(circle at 85% 8%, rgba(245,158,11,.10), transparent 26%),' +
    'radial-gradient(circle at 15% 88%, rgba(180,83,9,.06), transparent 28%),' +
    '#fdfaf6',
  mainBackgroundDark:
    'radial-gradient(circle at 85% 8%, rgba(245,158,11,.07), transparent 26%),' +
    'radial-gradient(circle at 15% 88%, rgba(180,83,9,.05), transparent 28%),' +
    '#15100b',

  /** Gradiente do botão primário. */
  primaryGradient: 'linear-gradient(135deg, #d97706, #92400e)',
  /** Gradiente da marca. */
  brandGradient: 'linear-gradient(135deg, #f59e0b, #b45309)',
} as const
